import { CRITERION_VALUE } from "@/constants/criterion-values"

interface CriterionEntry {
  result: string
  identity: string | null
  index: number
}

interface CriteriaAgreementCounts {
  matching: number
  total: number
}

interface MachineCompositeCriteriaSource {
  set_value?: string[] | null
  result?: Record<string, unknown> | null
  result_json?: Record<string, unknown> | null
  json_value?: Record<string, unknown> | null
}

interface HumanCompositeCriteriaSource {
  set_value?: string[] | null
  json_value?: Record<string, unknown> | null
}

function normalizeCriterion(criterion: string): string {
  return criterion.trim().replace(/\s+/g, " ").toLowerCase()
}

function extractIndividualEntries(
  raw: Record<string, unknown> | null | undefined,
): CriterionEntry[] | null {
  const results = (raw as { individual_results?: unknown } | null | undefined)
    ?.individual_results
  if (!Array.isArray(results) || results.length === 0) {
    return null
  }
  const occurrences = new Map<string, number>()
  return results.map((rawItem, index) => {
    const item = rawItem as
      | { result?: unknown; criterion?: unknown }
      | null
      | undefined
    const criterion =
      typeof item?.criterion === "string"
        ? normalizeCriterion(item.criterion)
        : ""
    const occurrence = criterion ? (occurrences.get(criterion) ?? 0) : 0
    if (criterion) {
      occurrences.set(criterion, occurrence + 1)
    }
    return {
      result:
        typeof item?.result === "string"
          ? item.result
          : CRITERION_VALUE.UNKNOWN,
      identity: criterion ? `${criterion}::${occurrence}` : null,
      index,
    }
  })
}

function extractMachineEntries(
  source: MachineCompositeCriteriaSource,
): CriterionEntry[] | null {
  for (const raw of [source.result, source.result_json, source.json_value]) {
    const entries = extractIndividualEntries(raw)
    if (entries) {
      return entries
    }
  }
  return source.set_value?.length
    ? source.set_value.map((result, index) => ({
        result,
        identity: null,
        index,
      }))
    : null
}

function extractHumanEntries(
  source: HumanCompositeCriteriaSource,
): CriterionEntry[] | null {
  const structured = extractIndividualEntries(source.json_value)
  if (!source.set_value?.length) {
    return null
  }
  return source.set_value.map((result, index) => ({
    result,
    identity: structured?.[index]?.identity ?? null,
    index,
  }))
}

export function countPairedCompositeCriteriaAgreements(
  machineSource: MachineCompositeCriteriaSource,
  humanSource: HumanCompositeCriteriaSource,
): CriteriaAgreementCounts | null {
  const machineCriteria = extractMachineEntries(machineSource)
  const humanCriteria = extractHumanEntries(humanSource)
  if (!machineCriteria || !humanCriteria) {
    return null
  }

  const machineByIdentity = new Map(
    machineCriteria
      .filter((entry): entry is CriterionEntry & { identity: string } =>
        Boolean(entry.identity),
      )
      .map((entry) => [entry.identity, entry]),
  )
  let matching = 0
  let total = 0
  for (const human of humanCriteria) {
    if (
      human.result !== CRITERION_VALUE.MET &&
      human.result !== CRITERION_VALUE.NOT_MET
    ) {
      continue
    }
    const identityMatch = human.identity
      ? machineByIdentity.get(human.identity)
      : undefined
    const indexedMachine = machineCriteria[human.index]
    const machine =
      identityMatch ??
      (human.identity == null || indexedMachine?.identity == null
        ? indexedMachine
        : undefined)
    if (!machine) {
      continue
    }
    total++
    if (machine.result === human.result) {
      matching++
    }
  }
  return { matching, total }
}
